// ===============================================================

// 📤 OutgoingCallPage.js — FINAL SAFE VERSION (FIXED)

// ===============================================================



import React, { useEffect, useMemo, useRef, useState } from "react";

import { useParams, useNavigate, useLocation } from "react-router-dom";

import { useTranslation } from "react-i18next";

import { db, auth } from "../firebase";

import { doc, onSnapshot } from "firebase/firestore";

import { getFunctions, httpsCallable } from "firebase/functions";

import {

  prefetchZegoBootstrap,

} from "./callMediaWarmup";

import { callDebugLog } from "./callDebug";
import { resolveCallRoomRoute } from "./callRoute";
import { resolveOutgoingCallMessage } from "./outgoingCallOutcome";
import {
  buildReturnContextFromLocation,
  normalizeReturnContext,
  saveReturnContext,
  contextFromLegacyReturnTo,
} from "./nativeCallReturnContext";
import { clearCallStartupTrace } from "./callStartupTrace";
import {
  beginNativeCallPreparation,
  clearNativeCallPreparation,
} from "./nativeCallPreparation";

import "./OutgoingCallPage.css";



export default function OutgoingCallPage() {

  const { t } = useTranslation();

  const { callId } = useParams();

  const navigate = useNavigate();

  const location = useLocation();



  const returnTo = location?.state?.returnTo || "/home";
  const incomingReturnContext = useMemo(() => {
    return (
      normalizeReturnContext(location?.state?.returnContext) ||
      contextFromLegacyReturnTo(returnTo)
    );
  }, [location?.state?.returnContext, returnTo]);



  const [status, setStatus] = useState("loading");
  const [endedReason, setEndedReason] = useState(null);
  const [calleeName, setCalleeName] = useState("");

  const leavingRef = useRef(false);
  const navigatedToRoomRef = useRef(false);

  const zegoBootstrapRef = useRef(null);

  const prefetchStartedRef = useRef(false);

  const warmupSkippedRef = useRef(false);

  const mountPerfRef = useRef(performance.now());

  const prevStatusRef = useRef(null);



  useEffect(() => {
    mountPerfRef.current = performance.now();
    prevStatusRef.current = null;
    navigatedToRoomRef.current = false;
    prefetchStartedRef.current = false;
    warmupSkippedRef.current = false;
  }, [callId]);

  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!callId || !uid || !incomingReturnContext) return;
    saveReturnContext(uid, callId, incomingReturnContext);
    console.log(
      `[callReturn] captured role=caller callId=${callId} url=${incomingReturnContext.url}`
    );
  }, [callId, incomingReturnContext]);



  // ============================================================

  // 🎫 Token prefetch (background, once per call). Media capture is CallRoom-only.

  // ============================================================

  useEffect(() => {

    if (!callId) return;



    const activeStatuses = ["loading", "ringing", "ongoing"];

    if (!activeStatuses.includes(status)) return;



    if (!warmupSkippedRef.current) {
      warmupSkippedRef.current = true;
    }



    if (prefetchStartedRef.current) return;

    prefetchStartedRef.current = true;

    const userName =

      auth.currentUser?.displayName ||

      auth.currentUser?.email?.split("@")[0] ||

      "User";

    const uid = auth.currentUser?.uid;

    // Phase N2A: prepares Zego bootstrap + native Firebase auth in parallel
    // while still ringing, so Accept->native-open has nothing left to await.
    // Falls back to a direct prefetch if there is somehow no signed-in user
    // yet (should not normally happen on this route).
    if (uid) {
      const entry = beginNativeCallPreparation({ callId, uid, userName, role: "caller" });
      entry?.bootstrapPromise?.then((bootstrap) => {
        if (bootstrap) {
          zegoBootstrapRef.current = bootstrap;
          console.log("[callPerf] token_prefetch_success", {
            callId,
            elapsed: Math.round(performance.now() - mountPerfRef.current),
          });
        }
      });
    } else {
      console.log("[callPerf] token_prefetch_start", { callId });
      void prefetchZegoBootstrap(callId, userName).then((bootstrap) => {
        if (bootstrap) {
          zegoBootstrapRef.current = bootstrap;
          console.log("[callPerf] token_prefetch_success", {
            callId,
            elapsed: Math.round(performance.now() - mountPerfRef.current),
          });
        }
      });
    }

  }, [callId, status]);



  // ============================================================

  // 👂 مراقبة وثيقة المكالمة فقط (READ ONLY)

  // ============================================================

  useEffect(() => {

    if (!callId) {

      setStatus("invalid_id");

      return;

    }



    const ref = doc(db, "calls", callId);



    const unsub = onSnapshot(

      ref,

      (snap) => {

        if (!snap.exists()) {

          setStatus("not_found");

          return;

        }



        const data = snap.data();

        const nextStatus = data.status || "ringing";

        prevStatusRef.current = nextStatus;
        setStatus(nextStatus);
        setEndedReason(data.endedReason || null);
        const snapshotName = String(
          data?.femaleSnapshot?.displayName || ""
        ).trim();
        if (snapshotName) setCalleeName(snapshotName);

        if (nextStatus === "ongoing" && !navigatedToRoomRef.current) {
          navigatedToRoomRef.current = true;

          const { target } = resolveCallRoomRoute({
            callId,
            role: "caller",
            source: "outgoing_call_ongoing",
          });

          navigate(target, {
            replace: true,
            state: {
              returnTo: incomingReturnContext?.url || returnTo,
              returnContext: incomingReturnContext || undefined,
              zegoBootstrap: zegoBootstrapRef.current || undefined,
            },
          });
        }

      },

      () => setStatus("error")

    );



    return () => unsub();

  }, [callId, navigate, returnTo, incomingReturnContext]);



  // ============================================================

  // 🔴 إلغاء المكالمة (عن طريق Cloud Function فقط)

  // ============================================================

  const handleCancel = () => {

    if (leavingRef.current) return;

    leavingRef.current = true;



    callDebugLog("[callHangup] tapped", { callId, source: "OutgoingCallPage" });
    callDebugLog("[callDebug] endCall called from", "OutgoingCallPage:handleCancel", {
      callId,
      currentUid: auth.currentUser?.uid,
    });

    clearNativeCallPreparation(callId);
    clearCallStartupTrace(callId);

    navigate(returnTo, { replace: true });



    const functions = getFunctions(undefined, "us-central1");

    const endCall = httpsCallable(functions, "endCall");



    void endCall({ callId, reason: "cancelled" })

      .then(() => {

        callDebugLog("[callHangup] endCall done", { callId });

      })

      .catch((e) => {

        console.error("[callHangup] endCall failed", {

          callId,

          error: e?.message || e,

        });

      });

  };



  // ============================================================

  // 🧠 نص الحالة

  // ============================================================

  // Phase N9D: distinct outcome messages by the verified server
  // endedReason (see functions/calls/rejectCall.js "rejected",
  // functions/calls/autoTimeoutCall.js "no_answer_timeout",
  // functions/calls/endCall.js "cancelled"/"cancelled_before_answer" for a
  // caller-initiated hangup). "status" itself is always "ended" for every
  // one of these - it is never "timeout"/"cancelled" server-side, so
  // endedReason is the only signal that can distinguish them (see
  // resolveOutgoingCallMessage for the pure, unit-tested decision).
  const message = resolveOutgoingCallMessage(
    { status, endedReason, calleeName },
    t
  );



  // ============================================================

  // 🔙 رجوع تلقائي عند الحالات النهائية

  // ============================================================

  useEffect(() => {

    if (

      ["ended", "cancelled", "timeout", "error", "not_found"].includes(status)

    ) {

      // Phase N2A: a call that never reached Accept must never leave
      // prepared native auth/Zego data sitting around for a stale callId.
      clearNativeCallPreparation(callId);
      clearCallStartupTrace(callId);

      const timerId = setTimeout(() => {
        callDebugLog("[callDebug] navigate away", {
          from: "OutgoingCallPage:finalStatus",
          callId,
          status,
          currentUid: auth.currentUser?.uid,
        });
        navigate(returnTo, { replace: true });
      }, 2000);



      return () => clearTimeout(timerId);

    }

  }, [status, navigate, returnTo]);



  // ============================================================

  // UI

  // ============================================================

  return (

    <div className="outgoing-root">

      <div className="outgoing-box">

        <div className="outgoing-title">{t("calls.outgoing.title", { defaultValue: "Outgoing call" })}</div>

        <div className="outgoing-text">{message}</div>



        {status === "ringing" && (

          <button className="outgoing-cancel-btn" onClick={handleCancel}>

            {t("calls.actions.cancel_call", { defaultValue: "Cancel call" })}

          </button>

        )}

      </div>

    </div>

  );

}


